import { useState, useEffect, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const TESTIMONIALS = [
  {
    name: "Adaeze O.",
    location: "Lagos",
    quote:
      "I ordered a Bottega tote and an Apple MacBook Pro on the same week. Both arrived in under 8 days, perfectly packaged. MPS feels less like a shipping service and more like a private concierge.",
  },
  {
    name: "Chinedu A.",
    location: "Abuja",
    quote:
      "We have used MPS for office procurement for over a year — monitors, gaming chairs, branded merchandise for events. Always on time, always exact spec. The B2B service is genuinely excellent.",
  },
  {
    name: "Tolu B.",
    location: "Port Harcourt",
    quote:
      "My mother's birthday gift arrived in beautiful packaging with no invoice — exactly as I asked. The team WhatsApped me photos at every stage. Couldn't recommend more highly.",
  },
  {
    name: "Femi K.",
    location: "Kano",
    quote:
      "Ordered a 55-inch Samsung TV. Flat freight rate, no surprises at customs, delivered to my front door in 9 working days. Will use again.",
  },
];

export default function TestimonialCarousel() {
  const [index, setIndex] = useState(0);
  const next = useCallback(
    () => setIndex((i) => (i + 1) % TESTIMONIALS.length),
    [],
  );
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length),
    [],
  );

  useEffect(() => {
    const t = setInterval(next, 7000);
    return () => clearInterval(t);
  }, [next]);

  const current = TESTIMONIALS[index];

  return (
    <section
      data-testid="testimonials-section"
      className="bg-[#0A0A0A] text-[#F8F5F0] py-24 md:py-32"
    >
      <div className="container-mps">
        <div className="overline text-[#C9A96E] mb-4 text-center">Client Stories</div>
        <h2 className="font-serif text-4xl md:text-5xl text-center max-w-3xl mx-auto tracking-tight">
          Trusted by clients across <span className="italic text-[#C9A96E]">all 36 states</span>.
        </h2>
        <div className="gold-divider mx-auto mt-8" />

        <div className="relative mt-20 max-w-3xl mx-auto min-h-[260px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="flex justify-center gap-1 mb-8">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={16} className="fill-[#C9A96E] text-[#C9A96E]" />
                ))}
              </div>
              <blockquote
                data-testid="testimonial-quote"
                className="font-serif text-2xl md:text-3xl leading-snug text-[#F8F5F0]/90 italic"
              >
                &ldquo;{current.quote}&rdquo;
              </blockquote>
              <div className="mt-10 overline text-[#C9A96E]">
                {current.name} · {current.location}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-14 flex items-center justify-center gap-8">
            <button
              data-testid="testimonial-prev"
              onClick={prev}
              className="p-3 border border-[#C9A96E]/40 hover:bg-[#C9A96E] hover:text-[#0A0A0A] transition-colors"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="flex gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  data-testid={`testimonial-dot-${i}`}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 transition-all ${
                    i === index ? "bg-[#C9A96E] w-10" : "bg-[#F8F5F0]/20 w-6"
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
            <button
              data-testid="testimonial-next"
              onClick={next}
              className="p-3 border border-[#C9A96E]/40 hover:bg-[#C9A96E] hover:text-[#0A0A0A] transition-colors"
              aria-label="Next testimonial"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
